import { useMutation, useQueryClient } from '@tanstack/react-query';

import { invalidateGamifyTrialExtension } from '@/hooks/query/useQueryGamifyTrialExtension';
import { GET_USER } from '@/hooks/query/useQueryUser';
import {
  dismissPostNoteQualityPanel,
  markPostNoteQualityPanelSeen,
  markPostNoteQualityTaskCompleted,
} from '@/pages/api/scribeAPIV2';
import { logger } from '@/utils/logger/logger';
import { type ImproveNotesTaskId } from '@heidi/growth/contracts';
import { type UserResponse } from '@heidi/workspace/contracts';
import { invalidateCurrentUserComposite } from '@heidi/workspace/data';

const MARK_POST_NOTE_QUALITY_TASK = 'useMutateMarkPostNoteQualityTask';
const DISMISS_POST_NOTE_QUALITY_PANEL = 'useMutateDismissPostNoteQualityPanel';
const MARK_POST_NOTE_QUALITY_PANEL_SEEN = 'useMutateMarkPostNoteQualityPanelSeen';

interface OptimisticUserContext {
  previousUser: UserResponse | undefined;
}

/**
 * GRO-3270: persists a completed "Improve your notes" task on the user.
 * Optimistically appends to `post_note_quality_completed_tasks` so the panel
 * card checks off immediately; the snapshot is restored on error and the
 * user query re-synced on settle. Errors are logged, not toasted — this
 * fires from passive evidence-of-use signals and must never interrupt the
 * user.
 */
export function useMutateMarkPostNoteQualityTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [MARK_POST_NOTE_QUALITY_TASK],
    mutationFn: async (taskId: ImproveNotesTaskId) => {
      await markPostNoteQualityTaskCompleted(taskId);
    },
    onMutate: (taskId: ImproveNotesTaskId): OptimisticUserContext => {
      const previousUser = queryClient.getQueryData<UserResponse>([GET_USER]);
      queryClient.setQueryData([GET_USER], (oldData: UserResponse) => {
        if (!oldData) {
          return undefined;
        }
        const completed = oldData.post_note_quality_completed_tasks ?? [];
        if (completed.includes(taskId)) {
          return oldData;
        }
        return {
          ...oldData,
          post_note_quality_completed_tasks: [...completed, taskId],
        };
      });
      return { previousUser };
    },
    onError: (error, _taskId, context) => {
      if (context?.previousUser) {
        queryClient.setQueryData([GET_USER], context.previousUser);
      }
      logger.error('Failed to mark post-note quality task as completed:', error);
    },
    onSettled: () => {
      void invalidateCurrentUserComposite();
      // GRO-3284: completing a task can cross a reward milestone (+7 / +14 trial
      // days), which the backend grants synchronously on this endpoint. Refresh
      // the gamify trial-extension + billing queries so the sidebar trial-day
      // count and progress update without a page reload.
      void invalidateGamifyTrialExtension();
    },
  });
}

/**
 * GRO-3270: permanently dismisses the "Improve your notes" panel.
 * Optimistically sets `post_note_quality_panel_dismissed_at` so the panel and
 * its launcher disappear immediately; the snapshot is restored on error and
 * the user query re-synced on settle.
 */
export function useMutateDismissPostNoteQualityPanel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [DISMISS_POST_NOTE_QUALITY_PANEL],
    mutationFn: async () => {
      await dismissPostNoteQualityPanel();
    },
    onMutate: (): OptimisticUserContext => {
      const previousUser = queryClient.getQueryData<UserResponse>([GET_USER]);
      queryClient.setQueryData([GET_USER], (oldData: UserResponse) => {
        if (!oldData) {
          return undefined;
        }
        return {
          ...oldData,
          post_note_quality_panel_dismissed_at: new Date().toISOString(),
        };
      });
      return { previousUser };
    },
    onError: (error, _variables, context) => {
      if (context?.previousUser) {
        queryClient.setQueryData([GET_USER], context.previousUser);
      }
      logger.error('Failed to dismiss post-note quality panel:', error);
    },
    onSettled: () => {
      void invalidateCurrentUserComposite();
    },
  });
}

/**
 * GRO-3284: marks the "Improve your notes" panel as seen so the `first_view`
 * analytics prop reports false on subsequent opens. Optimistically sets
 * `has_seen_improve_notes_panel` and is fire-and-forget — errors are logged,
 * never surfaced (this fires passively on first panel view).
 */
export function useMutateMarkPostNoteQualityPanelSeen() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [MARK_POST_NOTE_QUALITY_PANEL_SEEN],
    mutationFn: async () => {
      await markPostNoteQualityPanelSeen();
    },
    onMutate: (): OptimisticUserContext => {
      const previousUser = queryClient.getQueryData<UserResponse>([GET_USER]);
      queryClient.setQueryData([GET_USER], (oldData: UserResponse) => {
        if (!oldData) {
          return undefined;
        }
        return { ...oldData, has_seen_improve_notes_panel: true };
      });
      return { previousUser };
    },
    onError: (error, _variables, context) => {
      if (context?.previousUser) {
        queryClient.setQueryData([GET_USER], context.previousUser);
      }
      logger.error('Failed to mark post-note quality panel as seen:', error);
    },
    onSettled: () => {
      void invalidateCurrentUserComposite();
    },
  });
}
